import React, { useState, useCallback, useRef } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import Modal from "react-modal";
import { MdCrop } from "react-icons/md";
import { useTranslation } from "react-i18next";
import "react-image-crop/dist/ReactCrop.css";

export function CropModal({
  isOpen,
  onClose,
  imageSrc,
  onCropComplete,
  aspectRatio = 1521 / 320
}) {
  const { t } = useTranslation();
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const crop = centerCrop(
      makeAspectCrop({ unit: "%", width: 80 }, aspectRatio, width, height),
      width,
      height
    );
    setCrop(crop);
  };

  const getCroppedImage = useCallback(() => {
    if (!completedCrop || !imgRef.current) return;

    const canvas = document.createElement("canvas");
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

    canvas.width = 1521;
    canvas.height = 320;
    const ctx = canvas.getContext("2d");

    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;

    ctx.drawImage(
      imgRef.current,
      cropX,
      cropY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      1521,
      320
    );

    canvas.toBlob(
      async (blob) => {
        const croppedFile = new File([blob], "cropped_banner.png", {
          type: "image/png",
        });
        onCropComplete(croppedFile);
        onClose();
      },
      "image/png",
      1
    );
  }, [completedCrop, onClose, onCropComplete]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={t("CROP_MODAL.CROP_BANNER")}
      className='bg-white dark:bg-gray-800 rounded-xl shadow-xl w-[90%] max-w-4xl h-[70vh] mx-auto p-4 flex flex-col outline-none'
      overlayClassName='fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50'
    >
      <h2 className='text-lg font-semibold mb-3 flex items-center gap-2 text-gray-900 dark:text-white'>
        <MdCrop className="text-blue-600 dark:text-blue-400" />
        {t("CROP_MODAL.CROP_BANNER")} (1521×320)
      </h2>

      <div className='flex-1 overflow-auto pr-1'>
        {imageSrc && (
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspectRatio}
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt={t("CROP_MODAL.CROP_PREVIEW")}
              onLoad={onImageLoad}
              className='max-w-full h-auto mx-auto rounded-md'
            />
          </ReactCrop>
        )}
      </div>

      <div className='flex justify-end mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800'>
        <button
          type='button'
          className='px-4 py-2 bg-gray-400 dark:bg-gray-600 text-white rounded-md mr-2 hover:bg-gray-500 dark:hover:bg-gray-700 transition-colors'
          onClick={onClose}
        >
          {t("COMMON.CANCEL")}
        </button>
        <button
          type='button'
          className='px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors'
          onClick={getCroppedImage}
        >
          {t("CROP_MODAL.CROP_CONTINUE")}
        </button>
      </div>
    </Modal>
  );
}